import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { WorkbookPage, SectionTitle, Paragraph, Callout } from '@/components/WorkbookPage';
import { Calculator } from 'lucide-react';

export const WeeklyCalculatorPage = () => {
  const { formData, updateFormData } = useWorkbook();
  const [weeks, setWeeks] = useState(Array(8).fill({ income: '', notes: '' }));
  const [calculated, setCalculated] = useState({ weekly: 0, monthly: 0 });
  
  const updateWeek = (index, field, value) => {
    const newWeeks = [...weeks];
    newWeeks[index] = { ...newWeeks[index], [field]: value };
    setWeeks(newWeeks);
  };
  
  const calculateAverage = () => {
    const validIncomes = weeks
      .map(w => parseFloat(w.income.replace(/[^0-9.]/g, '')))
      .filter(n => !isNaN(n) && n > 0);
    
    if (validIncomes.length > 0) {
      const total = validIncomes.reduce((a, b) => a + b, 0);
      const weeklyAvg = total / validIncomes.length;
      setCalculated({
        weekly: weeklyAvg.toFixed(2),
        monthly: (weeklyAvg * 4.33).toFixed(2)
      });
    }
  };
  
  return (
    <WorkbookPage pageId="weekly-calculator" prevPage="glossary" nextPage="check-in-sheets">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <Calculator className="h-6 w-6 text-primary" />
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
            Weekly Average Calculator
          </h1>
        </div>
        <p className="text-muted-foreground">
          Track 4-8 weeks of income to find your reliable average
        </p>
      </header>
      
      <Callout type="info">
        This is especially useful if you have irregular income. Track at least 4 weeks 
        (8 is better) to get a realistic picture of what you can count on.
      </Callout>
      
      <SectionTitle>Enter Your Weekly Income</SectionTitle>
      
      <div className="overflow-x-auto my-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-border">
              <th className="text-left py-3 pr-4">Week</th>
              <th className="text-right py-3 px-4">Income</th>
              <th className="text-left py-3 pl-4">Notes (optional)</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, index) => (
              <tr key={index} className="border-b border-border/50">
                <td className="py-3 pr-4 font-medium">Week {index + 1}</td>
                <td className="py-3 px-4">
                  <input
                    type="text"
                    className="w-full text-right bg-muted/30 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="$0.00"
                    value={week.income}
                    onChange={(e) => updateWeek(index, 'income', e.target.value)}
                  />
                </td>
                <td className="py-3 pl-4">
                  <input
                    type="text"
                    className="w-full bg-muted/30 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Extra shifts, slow week, etc."
                    value={week.notes}
                    onChange={(e) => updateWeek(index, 'notes', e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <button
        onClick={calculateAverage}
        className="w-full bg-primary text-primary-foreground py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
      >
        Calculate My Average
      </button>
      
      {(calculated.weekly > 0) && (
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="bg-primary/10 rounded-xl p-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">Weekly Average</p>
            <p className="text-3xl font-bold text-primary">${calculated.weekly}</p>
          </div>
          <div className="bg-success/10 rounded-xl p-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">Monthly Estimate (×4.33)</p>
            <p className="text-3xl font-bold text-success">${calculated.monthly}</p>
          </div>
        </div>
      )}
      
      <div className="mt-8 p-4 bg-secondary/50 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <strong>Tip:</strong> If your income varies a lot, use the lower weeks more than 
          the higher weeks for planning. It's better to be pleasantly surprised than short.
        </p>
      </div>
    </WorkbookPage>
  );
};
